import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { get, getDatabase, ref } from 'firebase/database';

marked.use({
    tokenizer: {
        del(src) {
            // Returning false or undefined tells marked to skip this rule
            // and treat the text as plain, standard inline characters.
            return undefined;
        }
    }
});

export const get_numbers = (input: string): number[] => {
    var ret = input.toString().match(/[0-9]+/g);
    if (ret == null) {
        return [];
    }

    return ret.map(x => parseInt(x));
};

export const cleanFilenameForDisplay = (filename: string): string => {
    var display = filename ? filename.replace('.pdf', '') : 'Unknown File';
    const uuidWithUnderscoreRegex: RegExp =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    if (
        uuidWithUnderscoreRegex.test(display) &&
        display.split('_').length > 1
    ) {
        if (display.split('_')[1] === 'Flightline') {
            display = 'Flightline Report';
        }
    }
    return display;
};

// GFM requires the separator row to have the same column count as the header.
// Caught a case where the separator had 13 columns vs. 14 in the header, which
// causes marked to reject the whole block as a table and fall back to <p>.
// This pads the separator to match the header count before parsing.
export const fixTableSeparators = (markdown: string): string => {
    const lines = markdown.split('\n');
    const result: string[] = [];
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const prev = result[result.length - 1];
        if (prev && /^\|/.test(prev) && /^\|[-| :]+\|/.test(line)) {
            const headerCols = prev.split('|').length - 2;
            const sepCols = line.split('|').length - 2;
            if (sepCols < headerCols) {
                const missing = headerCols - sepCols;
                result.push(line.replace(/\|$/, '|---'.repeat(missing) + '|'));
                continue;
            }
        }
        result.push(line);
    }
    return result.join('\n');
};

// Citations inside table cells contain ||| delimiters that GFM misreads as
// column separators, breaking the table. Extracting them to just after the
// table block matches the pattern already seen in natural LLM output.
export const extractCitationsFromTableCells = (markdown: string): string => {
    const lines = markdown.split('\n');
    const result: string[] = [];
    let pendingCitations: string[] = [];
    let inTable = false;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const isTableRow = /^\s*\|/.test(line);

        if (!isTableRow && inTable && pendingCitations.length > 0) {
            result.push('');
            result.push(...pendingCitations);
            pendingCitations = [];
        }

        inTable = isTableRow;

        if (isTableRow) {
            const citations: string[] = [];
            const stripped = line.replace(/\[\[\[([\s\S]*?)\]\]\]/g, match => {
                citations.push(match);
                return '';
            });
            if (citations.length > 0) {
                pendingCitations.push(...citations);
                result.push(stripped);
            } else {
                result.push(line);
            }
        } else {
            result.push(line);
        }
    }

    if (pendingCitations.length > 0) {
        result.push('');
        result.push(...pendingCitations);
    }

    return result.join('\n');
};

// Single ~ used as "approximately" (e.g. ~$5M) can pair with another ~ later
// in the same paragraph, causing marked to wrap everything between them in <del>.
// Escaping isolated ~ (not part of ~~) prevents this misinterpretation.
const escapeSingleTildes = (markdown: string): string =>
    markdown.replace(/(?<!~)~(?!~)/g, '\\~');

export const convertMarkdown = (markdown: string): string => {
    if (!markdown) {
        return 'ERROR: No message content';
    }

    const fixed = escapeSingleTildes(
        fixTableSeparators(extractCitationsFromTableCells(markdown))
    );
    const rawHtmlWithDefault = marked.parse(fixed, { async: false });
    return DOMPurify.sanitize(rawHtmlWithDefault);
};

function downloadStringAsFile(text, fileName, mimeType = 'text/plain') {
    const blob = new Blob([text], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export const generateVariablesOutputDownload = async (
    companyId: string,
    variables: string[] = null
) => {
    const varsPath = `flowtasks/vars/${companyId}`;
    const varsRef = ref(getDatabase(), varsPath);
    const snapshot = await get(varsRef);
    if (!snapshot.exists()) {
        alert('No variables data found for export.');
        return;
    }

    const varsData = snapshot.val();
    const reportExportDate = `Report Generation Date: ${new Date().toLocaleString()}`;

    var filtered = varsData;
    if (variables && variables.length > 0) {
        filtered = Object.fromEntries(
            Object.entries(varsData).filter(([key]) => variables.includes(key))
        );
    }

    const outputData = Object.entries(filtered)
        .map(([varName, content]) => {
            return `--- FLIGHTLINE VARIABLE ${varName} ---:\n${content}\n\n`;
        })
        .join('');
    const outputWithDate = `${reportExportDate}\n\n${outputData}`;
    var fileName = `output_vars_${companyId}.md`;
    if (variables && variables.length === 1) {
        fileName = `output_${variables[0]}_${companyId}.md`;
    }

    downloadStringAsFile(outputWithDate, fileName, 'text/markdown');
};
